const PENDING = 'PENDING'
const SUCCESS = 'SUCCESS'
const FAILURE = 'FAILURE'

const ASYNC_SYMBOL = '(async)'

// Temporary store for keeping resolver/rejecter functions for in-flight promises.
const PROMISE_STORE = {}

// Checks if the action type given is an async action type. We expect an *exact* match
// with the async symbol at the beginning of the type.
const _isAsyncActionType = (type) => {
  return type.slice(0, ASYNC_SYMBOL.length) === ASYNC_SYMBOL
}

// Helpers for checking async actions.
const _isAsyncAction = (a) => _isAsyncActionType(a.type)
const _isPendingAction = (a) => _isAsyncAction(a) && a.type.indexOf(PENDING) > 0
const _isSuccessAction = (a) => _isAsyncAction(a) && a.type.indexOf(SUCCESS) > 0
const _isFailureAction = (a) => _isAsyncAction(a) && a.type.indexOf(FAILURE) > 0

/**
 * Makes an async action type. Using this util will allow you to refer to *one*
 * type which has 3 props on it: PENDING, SUCCESS and FAILURE.
 *
 * Example:
 *   const GET_REGULARS = asyncActionType('GET_REGULARS')
 *
 */
export const asyncActionType = (type) => {
  const actionType = function(){}
  actionType.toString = () => type

  actionType.PENDING = `${ASYNC_SYMBOL} ${type}:${PENDING}`
  actionType.SUCCESS = `${ASYNC_SYMBOL} ${type}:${SUCCESS}`
  actionType.FAILURE = `${ASYNC_SYMBOL} ${type}:${FAILURE}`

  return actionType
}

const _getAsyncActionType = (type) => {
  return type.replace(PENDING, '').replace(SUCCESS, '').replace(FAILURE, '')
}

/**
 * Makes an action using a type string and either a payload or error. If both payload
 * and error are given, payload has priority. Returns an object with `type` and either
 * a `payload` or an `error` property.
 */
export const makeAction = (type, { payload, error } = {}) => {
  const action = { type }

  // prioritise payload
  if (payload) {
    action.payload = payload
  } else if (error) {
    action.error = error
  }

  return action
}

/**
 * Middleware which hooks into the async action machinery in order to return a promise
 * to the dispatcher of the action for tracking the async action progress. This is very
 * useful when the caller needs to know when the promise resolves or rejects for performing
 * certain actions. For example, using promises makes using Formik with sagas much easier
 * cause we don't need to propagate the action state to the store, we can just listen on
 * the promise *in the component* that includes the form.
 */
export const middleware = store => next => action => {
  // If pending, return a promise and store the resolve/reject functions for further
  // processing when the action succeeds or fails.
  if (_isPendingAction(action)) {
    const promise = new Promise((resolve, reject) => {
      PROMISE_STORE[_getAsyncActionType(action.type)] = { resolve, reject }
    })
    next(action)
    return promise
  }
  
  next(action)

  // Nothing further to do here.
  if (!_isAsyncAction(action)) return

  // At this point we have a success or failure so we need to call the appropriate function
  // and perform cleanup.
  if (PROMISE_STORE[_getAsyncActionType(action.type)] && !_isPendingAction(action)) {
    const { resolve, reject } = PROMISE_STORE[_getAsyncActionType(action.type)]
    delete PROMISE_STORE[_getAsyncActionType(action.type)]
    if (_isSuccessAction(action) && resolve) resolve(action.payload)
    if (_isFailureAction(action) && reject) reject(action.error)
  }
}


// WEBPACK FOOTER //
// ./src/redux/action.js